import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';

import './DateRangeSelector.css';
import { startOfDay, endOfDay } from 'date-fns';
import { DateRangeType } from 'services/hooks/queries';

const DateRangeSelector = ({
    label = 'Date Range',
    onChange,
    initialDateRange = 'last7Days',
    showCustomRange = true,
}) => {
    const [dateRangeType, setDateRangeType] = useState(initialDateRange);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [formattedDateRange, setFormattedDateRange] = useState('');

    useEffect(() => {
        updateDateRange(dateRangeType, false);
    }, []);

    const updateDateRange = (rangeType, isOnchangeTrigger = true) => {
        let newStartDate = new Date();
        let newEndDate = new Date();

        switch (rangeType) {
            case DateRangeType.today:
                // Start and end date both set to today
                newStartDate = new Date();
                newEndDate = new Date();
                break;
            case DateRangeType.yesterday:
                // Start and end date both set to yesterday
                newStartDate = new Date();
                newStartDate.setDate(newStartDate.getDate() - 1);
                newEndDate = new Date(newStartDate);
                break;
            case DateRangeType.last7Days:
                // Start date set to 7 days ago, end date set to today
                newStartDate = new Date();
                newStartDate.setDate(newStartDate.getDate() - 6);
                break;
            case DateRangeType.last30Days:
                // Start date set to 30 days ago, end date set to today
                newStartDate = new Date();
                newStartDate.setDate(newStartDate.getDate() - 29);
                break;
            case DateRangeType.custom:
                // Keep existing dates for custom range
                if (!startDate) {
                    newStartDate = new Date();
                    newStartDate.setDate(newStartDate.getDate() - 6);
                } else {
                    newStartDate = new Date(startDate);
                }

                if (!endDate) {
                    newEndDate = new Date();
                } else {
                    newEndDate = new Date(endDate);
                }
                break;
            default:
                break;
        }

        // Reset time to start of day for start date and end of day for end date
        newStartDate = startOfDay(newStartDate);
        newEndDate = endOfDay(newEndDate);

        setStartDate(newStartDate);
        setEndDate(newEndDate);

        // Format the date range for display
        formatDateRange(newStartDate, newEndDate);

        // Notify parent component
        if (onChange && isOnchangeTrigger) {
            onChange({
                startDate: newStartDate,
                endDate: newEndDate,
                dateRangeType: rangeType,
            });
        }
    };

    const formatDateRange = (start, end) => {
        if (!start || !end) return;

        const options = { month: 'short', day: 'numeric', year: 'numeric' };
        const startStr = start.toLocaleDateString('en-US', options);
        const endStr = end.toLocaleDateString('en-US', options);

        if (startStr === endStr) {
            setFormattedDateRange(startStr);
        } else {
            setFormattedDateRange(`${startStr} - ${endStr}`);
        }
    };

    const handleStartDateChange = (date) => {
        const newStartDate = startOfDay(new Date(date));

        // If start date is after end date, set end date to start date
        let newEndDate = endDate;
        if (newStartDate > endDate) {
            newEndDate = endOfDay(new Date(newStartDate));
            setEndDate(newEndDate);
        }

        setStartDate(newStartDate);
        formatDateRange(newStartDate, newEndDate);

        if (onChange) {
            onChange({
                startDate: newStartDate,
                endDate: newEndDate || new Date(),
                dateRangeType: DateRangeType.custom,
            });
        }
    };

    const handleEndDateChange = (date) => {
        const newEndDate = endOfDay(new Date(date));

        // If end date is before start date, set start date to end date
        let newStartDate = startDate;
        if (newEndDate < startDate) {
            newStartDate = startOfDay(new Date(newEndDate));
            setStartDate(newStartDate);
        }

        setEndDate(newEndDate);
        formatDateRange(newStartDate, newEndDate);

        if (onChange) {
            onChange({
                startDate: newStartDate || new Date(),
                endDate: newEndDate,
                dateRangeType: DateRangeType.custom,
            });
        }
    };

    const handleDateRangeTypeChange = (e) => {
        setDateRangeType(e.target.value);
        updateDateRange(e.target.value);
    };

    return (
        <div className='date-selector'>
            <div className='date-selector-controls'>
                <select className='date-selector-dropdown' value={dateRangeType} onChange={handleDateRangeTypeChange}>
                    <option value={DateRangeType.today}>Today</option>
                    <option value={DateRangeType.yesterday}>Yesterday</option>
                    <option value={DateRangeType.last7Days}>Last 7 Days</option>
                    <option value={DateRangeType.last30Days}>Last 30 Days</option>
                    {showCustomRange && <option value={DateRangeType.custom}>Custom Range</option>}
                </select>

                {dateRangeType === DateRangeType.custom ? (
                    <div className='date-selector-custom'>
                        <DatePicker
                            selected={startDate}
                            onChange={handleStartDateChange}
                            selectsStart
                            startDate={startDate}
                            endDate={endDate}
                            maxDate={new Date()}
                            className='date-selector-input'
                            dateFormat='MM/dd/yyyy'
                            placeholderText='Start Date'
                        />
                        <span className='date-selector-separator'>to</span>
                        <DatePicker
                            selected={endDate}
                            onChange={handleEndDateChange}
                            selectsEnd
                            startDate={startDate}
                            endDate={endDate}
                            minDate={startDate}
                            maxDate={new Date()}
                            className='date-selector-input'
                            dateFormat='MM/dd/yyyy'
                            placeholderText='End Date'
                        />
                    </div>
                ) : (
                    <div className='date-selector-display'>{formattedDateRange}</div>
                )}
            </div>
        </div>
    );
};

export default DateRangeSelector;
